import { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  ArrowLeft, Search, Plus, School, GraduationCap, Users, ThumbsUp, ThumbsDown,
  MessageCircle, UserPlus, X, Lock, Send, Sparkles, ShieldAlert, Clock,
  Trophy, Flame, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import { useSubscription } from "../context/SubscriptionContext";

const SCHOOL_TYPES = [
  { id: "primary",             label: "Szkoła podstawowa" },
  { id: "secondary_liceum",    label: "Liceum" },
  { id: "secondary_technikum", label: "Technikum" },
  { id: "secondary_zawodowka", label: "Szkoła branżowa/zawodowa" },
  { id: "university",          label: "Uczelnia wyższa" },
  { id: "kindergarten",        label: "Przedszkole" },
  { id: "other",               label: "Inna placówka" },
];
const TYPE_LABEL = Object.fromEntries(SCHOOL_TYPES.map((t) => [t.id, t.label]));

export default function SchoolHub({ onBack, onOpenPricing }) {
  // view: search | school | person
  const [view, setView] = useState("search");
  const [school, setSchool] = useState(null);
  const [person, setPerson] = useState(null);

  if (view === "person" && person) {
    return (
      <PersonView
        personId={person}
        onBack={() => setView(school ? "school" : "search")}
        onOpenPricing={onOpenPricing}
      />
    );
  }
  if (view === "school" && school) {
    return (
      <SchoolView
        schoolId={school}
        onBack={() => { setSchool(null); setView("search"); }}
        onOpenPerson={(pid) => { setPerson(pid); setView("person"); }}
        onOpenPricing={onOpenPricing}
      />
    );
  }
  return (
    <SearchView
      onBack={onBack}
      onPick={(sid) => { setSchool(sid); setView("school"); }}
      onOpenPerson={(pid) => { setPerson(pid); setView("person"); }}
    />
  );
}

/* -------------------- Search (city-first flow) -------------------- */
function SearchView({ onBack, onPick, onOpenPerson }) {
  const [selectedCity, setSelectedCity] = useState(null);

  const handlePick = (payload) => {
    // „Po nazwie szkoły" → payload zawiera _goto (school_id) i omijamy krok 2
    if (payload && payload._goto) {
      onPick(payload._goto);
      return;
    }
    setSelectedCity(payload);
  };

  if (!selectedCity) {
    return (
      <CityPickerView
        onBack={onBack}
        onPickCity={handlePick}
        onOpenPerson={onOpenPerson}
      />
    );
  }
  return (
    <CitySchoolsView
      city={selectedCity}
      onBack={() => setSelectedCity(null)}
      onPick={onPick}
    />
  );
}

/* ---- Krok 1: wybór miasta (z toggle „Po mieście / Po nazwie szkoły") ---- */
function CityPickerView({ onBack, onPickCity, onOpenPerson }) {
  const [mode, setMode] = useState("city"); // "city" | "name"
  const [q, setQ] = useState("");
  const [cities, setCities] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        if (mode === "city") {
          const { data } = await api.get("/schools/cities", { params: { q: q.trim(), limit: 20 } });
          if (!cancelled) { setCities(data.items || []); setSchools([]); }
        } else {
          if (q.trim().length < 2) {
            if (!cancelled) { setSchools([]); setCities([]); }
          } else {
            const { data } = await api.get("/schools/search", { params: { q: q.trim(), limit: 30 } });
            if (!cancelled) { setSchools(data.items || []); setCities([]); }
          }
        }
      } catch { if (!cancelled) { setCities([]); setSchools([]); } }
      finally { if (!cancelled) setLoading(false); }
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, mode]);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="school-search-view">
      <Header onBack={onBack} title="Moja szkoła" />
      <main className="flex-1 max-w-[900px] w-full mx-auto px-4 sm:px-6 py-8 sm:py-12 ss-fadein">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
          <School className="w-3.5 h-3.5" /> Społeczność
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
          {mode === "city" ? "Wybierz swoje miasto" : "Znajdź swoją szkołę"}
        </h1>
        <p className="mt-2 text-slate-500">
          {mode === "city"
            ? "Zacznij od miasta — potem wybierzesz szkołę z pełnej listy (ponad 55 000 placówek w Polsce)."
            : "Wpisz nazwę szkoły lub uczelni — przeszukamy całą Polskę. Nie musisz używać polskich znaków."}
        </p>

        {/* Toggle city / name */}
        <div className="mt-5 inline-flex items-center gap-1 bg-slate-100 rounded-full p-1" role="tablist" data-testid="search-mode-toggle">
          <button
            onClick={() => { setMode("city"); setQ(""); }}
            data-testid="search-mode-city"
            className={`text-xs font-bold px-3.5 py-2 rounded-full transition-colors ${
              mode === "city" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Po mieście
          </button>
          <button
            onClick={() => { setMode("name"); setQ(""); }}
            data-testid="search-mode-name"
            className={`text-xs font-bold px-3.5 py-2 rounded-full transition-colors ${
              mode === "name" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Po nazwie szkoły
          </button>
        </div>

        <div className="mt-4 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            data-testid={mode === "city" ? "city-search-input" : "name-search-input"}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={mode === "city"
              ? "Twoje miasto (np. Warszawa, rzeszow, lodz)..."
              : "Nazwa szkoły (np. Batorego, politechnika warszawska)..."}
            autoFocus
            className="w-full rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none pl-11 pr-4 py-4 text-base"
          />
        </div>

        {mode === "city" ? (
          <>
            <div className="mt-5 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
              {q.trim() ? "Pasujące miasta" : "Największe miasta"}
            </div>
            {loading && cities.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">Ładuję listę miast...</div>
            ) : cities.length === 0 ? (
              <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 text-center" data-testid="no-cities-found">
                <div className="text-sm text-slate-700 font-bold mb-1">Nie znaleźliśmy miasta „{q}"</div>
                <div className="text-xs text-slate-500">
                  Spróbuj „Po nazwie szkoły" powyżej albo napisz do nas: <a href="mailto:kontakt@socialstart.store" className="underline">kontakt@socialstart.store</a>
                </div>
              </div>
            ) : (
              <ul className="space-y-2" data-testid="city-list">
                {cities.map((c) => (
                  <li key={`${c.city}-${c.voivodeship}`}>
                    <button
                      data-testid={`city-${c.city}`}
                      onClick={() => onPickCity(c)}
                      className="w-full text-left bg-white border border-slate-200 hover:border-slate-900 transition-colors rounded-2xl p-4 flex items-center gap-3 group"
                    >
                      <div className="w-10 h-10 rounded-xl bg-[#FEF9C3] flex items-center justify-center flex-shrink-0">
                        <School className="w-5 h-5 text-slate-900" strokeWidth={2.5}/>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-900 truncate">{c.city}</div>
                        {c.voivodeship && <div className="text-xs text-slate-500 truncate">woj. {c.voivodeship}</div>}
                      </div>
                      <div className="text-xs font-bold text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1 whitespace-nowrap">
                        {c.count.toLocaleString("pl-PL")} {c.count === 1 ? "szkoła" : c.count < 5 ? "szkoły" : "szkół"}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <>
            <div className="mt-5 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Wyniki</div>
            {q.trim().length < 2 ? (
              <div className="py-6 text-center text-slate-400 text-sm">Wpisz co najmniej 2 znaki, żeby zobaczyć wyniki.</div>
            ) : loading && schools.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">Szukam...</div>
            ) : schools.length === 0 ? (
              <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 text-center" data-testid="no-schools-found-name">
                <div className="text-sm text-slate-700 font-bold mb-1">Nie znaleźliśmy „{q}"</div>
                <div className="text-xs text-slate-500">
                  Przełącz na „Po mieście" i wybierz z listy. Baza pochodzi z RSPO (MEiN).
                </div>
              </div>
            ) : (
              <ul className="space-y-2" data-testid="name-search-results">
                {schools.map((s) => (
                  <li key={s.school_id}>
                    <button
                      data-testid={`school-result-${s.school_id}`}
                      onClick={() => onPickCity({ city: s.city, voivodeship: s.voivodeship, _goto: s.school_id })}
                      className="w-full text-left bg-white border border-slate-200 hover:border-slate-900 transition-colors rounded-2xl p-4 flex items-center gap-3"
                    >
                      <div className="w-10 h-10 rounded-xl bg-[#FEF9C3] flex items-center justify-center flex-shrink-0">
                        <School className="w-5 h-5 text-slate-900" strokeWidth={2.5}/>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-900 truncate">{s.name}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {TYPE_LABEL[s.type] || s.type}
                          {s.city && ` · ${s.city}`}
                          {s.voivodeship && ` · woj. ${s.voivodeship}`}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        <p className="mt-6 text-[11px] text-slate-400 text-center">
          Baza RSPO · 55 925 placówek. Nie znajdujesz swojej szkoły? Napisz: <a href="mailto:kontakt@socialstart.store" className="underline">kontakt@socialstart.store</a>
        </p>

        {mode === "city" && !q.trim() && <WeeklyTop onOpenPerson={onOpenPerson} />}
      </main>
    </div>
  );
}

/* ---- Krok 2: lista szkół w wybranym mieście ---- */
function CitySchoolsView({ city, onBack, onPick }) {
  const [nameQ, setNameQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = { city: city.city, limit: 200 };
        if (nameQ.trim().length >= 2) params.q = nameQ.trim();
        if (typeFilter) params.type = typeFilter;
        const { data } = await api.get("/schools/search", { params });
        if (!cancelled) {
          setItems(data.items || []);
          setTotal(data.total ?? data.items?.length ?? 0);
        }
      } catch { if (!cancelled) { setItems([]); setTotal(0); } }
      finally { if (!cancelled) setLoading(false); }
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [city, nameQ, typeFilter]);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="city-schools-view">
      <Header onBack={onBack} title={city.city} />
      <main className="flex-1 max-w-[900px] w-full mx-auto px-4 sm:px-6 py-8 sm:py-12 ss-fadein">
        <button
          onClick={onBack}
          data-testid="change-city-button"
          className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-900 mb-3"
        >
          <ArrowLeft className="w-3 h-3" /> Zmień miasto
        </button>
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
          <School className="w-3.5 h-3.5" /> {city.voivodeship ? `woj. ${city.voivodeship}` : "Społeczność"}
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
          Szkoły w {city.city}
        </h1>
        <p className="mt-2 text-slate-500" data-testid="city-schools-count">
          {total > 0
            ? <>Znaleziono <b>{total.toLocaleString("pl-PL")}</b> {total === 1 ? "szkołę" : total < 5 ? "szkoły" : "szkół"}. Wybierz swoją lub przefiltruj.</>
            : "Brak szkół spełniających filtry."}
        </p>

        <div className="mt-6 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            data-testid="school-name-filter"
            value={nameQ}
            onChange={(e) => setNameQ(e.target.value)}
            placeholder="Filtruj po nazwie (np. Batorego, Kopernika)..."
            className="w-full rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none pl-11 pr-4 py-3 text-base"
          />
        </div>

        <div className="mt-3">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="school-type-filter"
            className="w-full rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none px-4 py-3 bg-white text-sm"
          >
            <option value="">Wszystkie typy placówek</option>
            {SCHOOL_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>

        {loading && items.length === 0 ? (
          <div className="py-10 text-center text-slate-400 text-sm">Ładuję szkoły w {city.city}...</div>
        ) : items.length > 0 ? (
          <ul className="mt-4 space-y-2" data-testid="school-search-results">
            {items.map((s) => (
              <li key={s.school_id}>
                <button
                  data-testid={`school-result-${s.school_id}`}
                  onClick={() => onPick(s.school_id)}
                  className="w-full text-left bg-white border border-slate-200 hover:border-slate-900 transition-colors rounded-2xl p-4 flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#FEF9C3] flex items-center justify-center flex-shrink-0">
                    <School className="w-5 h-5 text-slate-900" strokeWidth={2.5}/>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-slate-900 truncate">{s.name}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {TYPE_LABEL[s.type] || s.type}
                      {s.street && ` · ul. ${s.street}`}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-6 bg-slate-50 border border-slate-200 rounded-3xl p-6 text-center" data-testid="no-schools-found">
            <div className="text-sm text-slate-700 font-bold mb-1">Nie znaleziono szkół pasujących do filtrów.</div>
            <div className="text-xs text-slate-500">
              Usuń filtry lub zmień miasto. Baza pochodzi z RSPO (MEiN). Napisz: <a href="mailto:kontakt@socialstart.store" className="underline">kontakt@socialstart.store</a>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

/* -------------------- School detail -------------------- */
function SchoolView({ schoolId, onBack, onOpenPerson, onOpenPricing }) {
  const { subscription } = useSubscription();
  const isSub = subscription && subscription.plan !== "free";
  const [school, setSchool] = useState(null);
  const [people, setPeople] = useState([]);
  const [adding, setAdding] = useState(false);
  const [personName, setPersonName] = useState("");
  const [personRole, setPersonRole] = useState("teacher");

  const load = async () => {
    try {
      // Zawsze pobierz podstawowe dane szkoły
      const { data: s } = await api.get(`/schools/${schoolId}`);
      setSchool(s);
      // Listę osób pobieraj tylko dla subskrybentów
      if (isSub) {
        const { data: p } = await api.get(`/schools/${schoolId}/people`);
        setPeople(p.items);
      }
    } catch (e) {
      toast.error("Nie udało się załadować szkoły");
    }
  };
  useEffect(() => { load(); }, [schoolId, isSub]);

  // Free user wchodzi na szkołę → automatycznie pokaż pricing
  useEffect(() => {
    if (subscription && !isSub) {
      onOpenPricing?.("schoolhub");
    }
  }, [subscription, isSub]);

  const addPerson = async () => {
    if (!isSub) { onOpenPricing("add_person"); return; }
    const name = personName.trim();
    if (name.length < 2) { toast.error("Imię i nazwisko za krótkie"); return; }
    try {
      await api.post(`/schools/${schoolId}/people`, { name, role: personRole });
      toast.success("Dodano osobę");
      setAdding(false);
      setPersonName("");
      load();
    } catch (e) {
      if (e.response?.status === 402) { onOpenPricing("add_person"); return; }
      toast.error(e.response?.data?.detail || "Nie udało się dodać");
    }
  };

  const teachers = people.filter((p) => p.role === "teacher");
  const students = people.filter((p) => p.role === "student");

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="school-view">
      <Header onBack={onBack} title={school?.name || "Szkoła"} />
      <main className="flex-1 max-w-[900px] w-full mx-auto px-4 sm:px-6 py-8 sm:py-12 ss-fadein">
        {school && (
          <>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
              <School className="w-3.5 h-3.5" /> {TYPE_LABEL[school.type] || school.type}{school.city && ` · ${school.city}`}
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
              {school.name}
            </h1>
            {isSub && (
              <div className="mt-2 text-slate-500 text-sm">
                <Users className="inline w-3.5 h-3.5 mr-1" /> {people.length} {people.length === 1 ? "osoba" : "osoby"}
              </div>
            )}

            {!isSub ? (
              /* Free user – zablokowany podgląd + CTA */
              <div className="mt-8 bg-gradient-to-br from-[#FFF9DB] to-white border-2 border-[#FFD600] rounded-3xl p-7 sm:p-10 text-center" data-testid="schoolhub-locked">
                <div className="w-16 h-16 rounded-2xl bg-[#FFD600] flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-7 h-7 text-slate-900" strokeWidth={2.5}/>
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                  SchoolHub tylko w subskrypcji
                </h2>
                <p className="mt-3 text-slate-600 max-w-md mx-auto leading-relaxed">
                  Przeglądaj nauczycieli i uczniów, czytaj anonimowe komentarze, oceniaj plus/minus i dodawaj osoby. Dostępne we wszystkich planach SocialStart.
                </p>
                <button
                  onClick={() => onOpenPricing?.("schoolhub")}
                  data-testid="schoolhub-unlock-cta"
                  className="mt-6 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold px-6 py-3 rounded-2xl"
                >
                  <Sparkles className="w-4 h-4 text-[#FFD600]"/> Zobacz plany
                </button>
              </div>
            ) : (
              <>
                <div className="mt-6 flex items-center justify-between gap-3">
                  <h2 className="text-xl font-bold tracking-tight text-slate-900">Nauczyciele i uczniowie</h2>
                  <button
                    onClick={() => setAdding(true)}
                    data-testid="add-person-button"
                    className="inline-flex items-center gap-2 bg-[#FFD600] hover:bg-[#E6C200] text-slate-900 font-bold text-sm px-4 py-2 rounded-2xl"
                  >
                    <UserPlus className="w-4 h-4" strokeWidth={2.5}/>
                    Dodaj osobę
                  </button>
                </div>

                <Section title="Nauczyciele" count={teachers.length} testid="teachers-section">
                  {teachers.map((p) => <PersonCard key={p.person_id} p={p} onOpen={() => onOpenPerson(p.person_id)} />)}
                  {teachers.length === 0 && <Empty text="Nikt jeszcze nie dodał nauczycieli."/>}
                </Section>
                <Section title="Uczniowie / studenci" count={students.length} testid="students-section">
                  {students.map((p) => <PersonCard key={p.person_id} p={p} onOpen={() => onOpenPerson(p.person_id)} />)}
                  {students.length === 0 && <Empty text="Nikt jeszcze nie dodał uczniów."/>}
                </Section>
              </>
            )}
          </>
        )}

        {adding && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={() => setAdding(false)}>
            <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 ss-fadein" onClick={(e) => e.stopPropagation()} data-testid="add-person-modal">
              <div className="flex items-center justify-between mb-4">
                <div className="text-lg font-extrabold tracking-tight text-slate-900">Dodaj osobę</div>
                <button onClick={() => setAdding(false)} className="p-2 rounded-xl hover:bg-slate-100"><X className="w-4 h-4"/></button>
              </div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Imię i nazwisko</label>
              <input
                value={personName}
                onChange={(e) => setPersonName(e.target.value)}
                data-testid="add-person-name"
                className="w-full rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none px-4 py-3 mb-3"
              />
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Rola</label>
              <div className="grid grid-cols-2 gap-2 mb-4" role="radiogroup">
                {["teacher","student"].map((r) => (
                  <button
                    key={r}
                    onClick={() => setPersonRole(r)}
                    data-testid={`add-person-role-${r}`}
                    className={`inline-flex items-center justify-center gap-2 py-3 rounded-2xl border-2 font-bold text-sm ${
                      personRole === r ? "border-[#FFD600] bg-[#FEF9C3] text-slate-900" : "border-slate-200 text-slate-500"
                    }`}
                  >
                    {r === "teacher" ? <GraduationCap className="w-4 h-4"/> : <Users className="w-4 h-4"/>}
                    {r === "teacher" ? "Nauczyciel" : "Uczeń / student"}
                  </button>
                ))}
              </div>
              <button
                onClick={addPerson}
                data-testid="add-person-save"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#FFD600] hover:bg-[#E6C200] text-slate-900 font-bold px-6 py-3.5 rounded-2xl btn-yellow-glow"
              >
                <Plus className="w-4 h-4" strokeWidth={2.5}/> Zapisz
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Section({ title, count, testid, children }) {
  return (
    <section className="mt-8" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">{title}</h3>
        <span className="text-xs text-slate-400">{count}</span>
      </div>
      <ul className="grid sm:grid-cols-2 gap-2">{children}</ul>
    </section>
  );
}

function Empty({ text }) {
  return (
    <li className="col-span-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-center text-sm text-slate-500">
      {text}
    </li>
  );
}

function PersonCard({ p, onOpen }) {
  return (
    <li>
      <button
        onClick={onOpen}
        data-testid={`person-card-${p.person_id}`}
        className="w-full text-left bg-white border border-slate-200 hover:border-slate-900 transition-colors rounded-2xl p-4"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
            p.role === "teacher" ? "bg-[#FEF9C3]" : "bg-slate-100"
          }`}>
            {p.role === "teacher" ? <GraduationCap className="w-5 h-5 text-slate-900"/> : <Users className="w-5 h-5 text-slate-900"/>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-slate-900 truncate">{p.name}</div>
            <div className="text-xs text-slate-500">
              {p.role === "teacher" ? "Nauczyciel" : "Uczeń / student"}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="inline-flex items-center gap-0.5"><ThumbsUp className="w-3 h-3 text-green-600"/> {p.upvotes}</span>
            <span className="inline-flex items-center gap-0.5"><ThumbsDown className="w-3 h-3 text-red-500"/> {p.downvotes}</span>
            <span className="inline-flex items-center gap-0.5"><MessageCircle className="w-3 h-3"/> {p.comments_count}</span>
          </div>
        </div>
      </button>
    </li>
  );
}

/* -------------------- Person detail -------------------- */
function PersonView({ personId, onBack, onOpenPricing }) {
  const { subscription } = useSubscription();
  const isSub = subscription && subscription.plan !== "free";
  const [person, setPerson] = useState(null);
  const [comments, setComments] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [anon, setAnon] = useState(true);
  const [sending, setSending] = useState(false);
  const [voting, setVoting] = useState(false);
  const [mute, setMute] = useState(null); // { until, remaining_seconds, violations_count }

  // Live countdown
  useEffect(() => {
    if (!mute) return;
    const id = setInterval(() => {
      setMute((m) => {
        if (!m) return m;
        const next = m.remaining_seconds - 1;
        if (next <= 0) return null;
        return { ...m, remaining_seconds: next };
      });
    }, 1000);
    return () => clearInterval(id);
  }, [mute?.until]);

  // Free user wchodzi na profil osoby → automatycznie pokaż pricing
  useEffect(() => {
    if (subscription && !isSub) {
      onOpenPricing?.("schoolhub");
    }
  }, [subscription, isSub]);

  const applyMuteFromError = (detail) => {
    if (!detail || typeof detail !== "object") return false;
    if (detail.code === "user_muted" || detail.code === "content_blocked") {
      setMute({
        until: detail.timeout_until,
        remaining_seconds: detail.remaining_seconds,
        violations_count: detail.violations_count,
        reason: detail.code,
      });
      toast.error(detail.message || "Zostałeś wyciszony.", { duration: 6000 });
      return true;
    }
    return false;
  };

  const loadAll = async () => {
    try {
      const p = await api.get(`/people/${personId}`);
      setPerson(p.data);
      if (isSub) {
        const c = await api.get(`/people/${personId}/comments`);
        setComments(c.data);
      } else {
        setComments({ items: [], total: 0, locked: true });
      }
    } catch (e) {
      toast.error("Nie udało się załadować profilu");
    }
  };
  useEffect(() => { loadAll(); }, [personId, isSub]);

  // Sprawdź status moderacji przy wejściu
  useEffect(() => {
    if (!isSub) return;
    api.get("/moderation/status").then(({ data }) => {
      if (data?.muted) {
        setMute({
          until: data.timeout_until,
          remaining_seconds: data.remaining_seconds,
          violations_count: data.violations_count,
          reason: "existing",
        });
      }
    }).catch(() => {});
  }, [isSub]);

  const vote = async (value) => {
    if (!isSub) { onOpenPricing("vote"); return; }
    if (voting) return;
    setVoting(true);
    try {
      const next = person.my_vote === value ? 0 : value; // toggle off
      const { data } = await api.post(`/people/${personId}/vote`, { value: next });
      setPerson({ ...person, ...data });
    } catch (e) {
      if (e.response?.status === 402) onOpenPricing("vote");
      else if (e.response?.status === 403 && applyMuteFromError(e.response?.data?.detail)) { /* handled */ }
      else toast.error("Nie udało się zapisać głosu");
    } finally {
      setVoting(false);
    }
  };

  const submitComment = async () => {
    if (!isSub) { onOpenPricing("comment"); return; }
    if (mute) { toast.error("Jesteś wyciszony."); return; }
    const text = newComment.trim();
    if (text.length < 2) { toast.error("Komentarz za krótki"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/people/${personId}/comments`, { text, is_anonymous: anon });
      setComments({ ...comments, items: [data, ...(comments?.items || [])], total: (comments?.total || 0) + 1 });
      setNewComment("");
    } catch (e) {
      if (e.response?.status === 402) onOpenPricing("comment");
      else if (e.response?.status === 403 && applyMuteFromError(e.response?.data?.detail)) {
        setNewComment("");
      }
      else toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Nie udało się dodać");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="person-view">
      <Header onBack={onBack} title={person?.name || "Osoba"} />
      <main className="flex-1 max-w-[900px] w-full mx-auto px-4 sm:px-6 py-8 sm:py-12 ss-fadein">
        {person && (
          <>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
              {person.role === "teacher" ? <><GraduationCap className="w-3.5 h-3.5"/> Nauczyciel</> : <><Users className="w-3.5 h-3.5"/> Uczeń / student</>}
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
              {person.name}
            </h1>
            {person.school && (
              <div className="mt-1 text-slate-500 text-sm flex items-center gap-1.5">
                <School className="w-3.5 h-3.5"/>
                {person.school.name}
                {person.school.city && <> · {person.school.city}</>}
              </div>
            )}

            {/* Vote bar */}
            <div className="mt-6 flex items-center gap-3">
              <VoteButton
                active={person.my_vote === 1}
                onClick={() => vote(1)}
                icon={<ThumbsUp className="w-4 h-4"/>}
                count={person.upvotes}
                tone="up"
                testid="upvote-button"
                locked={!isSub}
              />
              <VoteButton
                active={person.my_vote === -1}
                onClick={() => vote(-1)}
                icon={<ThumbsDown className="w-4 h-4"/>}
                count={person.downvotes}
                tone="down"
                testid="downvote-button"
                locked={!isSub}
              />
              <div className="text-sm text-slate-500">
                Wynik: <b className={person.score >= 0 ? "text-green-700" : "text-red-600"}>{person.score > 0 ? "+" : ""}{person.score}</b>
              </div>
            </div>
            {!isSub && (
              <div className="mt-2 text-xs text-slate-400">
                <Lock className="inline w-3 h-3 mr-1"/> Oceny tylko dla subskrybentów.{" "}
                <button onClick={() => onOpenPricing("vote")} className="text-slate-900 underline font-bold">Odblokuj</button>
              </div>
            )}

            {/* Comments */}
            <section className="mt-10" data-testid="comments-section">
              <h2 className="text-xl font-bold tracking-tight text-slate-900">
                Komentarze {isSub && <span className="text-slate-400 text-sm font-semibold">({comments?.total || 0})</span>}
              </h2>

              {isSub ? (
                mute ? (
                  <MuteBanner mute={mute} />
                ) : (
                <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    data-testid="comment-text-input"
                    rows={3}
                    placeholder="Napisz coś..."
                    className="w-full rounded-xl border-2 border-slate-100 focus:border-[#FFD600] outline-none px-3 py-2 text-sm"
                  />
                  <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={anon}
                        onChange={(e) => setAnon(e.target.checked)}
                        data-testid="comment-anonymous-toggle"
                        className="accent-[#FFD600] w-4 h-4"
                      />
                      Napisz anonimowo
                    </label>
                    <button
                      onClick={submitComment}
                      disabled={sending}
                      data-testid="comment-submit"
                      className="inline-flex items-center gap-2 bg-[#FFD600] hover:bg-[#E6C200] text-slate-900 font-bold text-sm px-4 py-2 rounded-2xl disabled:opacity-50"
                    >
                      <Send className="w-4 h-4"/> Dodaj
                    </button>
                  </div>
                  <p className="mt-2 text-[11px] text-slate-400 leading-snug">
                    Komentarze są moderowane. Wulgaryzmy, rasizm, groźby i treści zakazane prawnie skutkują usunięciem wpisu i wyciszeniem konta.
                  </p>
                </div>
                )
              ) : (
                /* Free user – pełny lock bez pokazywania ile jest komentarzy */
                <div className="mt-4 bg-gradient-to-br from-[#FFF9DB] to-white border-2 border-[#FFD600] rounded-3xl p-7 sm:p-10 text-center" data-testid="comments-locked-full">
                  <div className="w-14 h-14 rounded-2xl bg-[#FFD600] flex items-center justify-center mx-auto mb-3">
                    <Lock className="w-6 h-6 text-slate-900" strokeWidth={2.5}/>
                  </div>
                  <div className="font-extrabold text-slate-900 text-lg">Komentarze dostępne tylko w subskrypcji</div>
                  <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
                    Zobacz, co inni myślą o nauczycielach i uczniach ze swojej szkoły. Pisz anonimowo lub pod imieniem.
                  </p>
                  <button
                    onClick={() => onOpenPricing("comment")}
                    data-testid="unlock-comments-button"
                    className="mt-5 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold px-5 py-2.5 rounded-2xl"
                  >
                    <Sparkles className="w-4 h-4 text-[#FFD600]"/> Zobacz plany
                  </button>
                </div>
              )}

              {isSub && (
                <ul className="mt-4 space-y-3" data-testid="comments-list">
                  {(comments?.items || []).map((c) => (
                    <li key={c.comment_id} className="bg-white border border-slate-200 rounded-2xl p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs font-bold text-slate-900">
                          {c.author_name}
                          {c.is_mine && <span className="ml-2 text-slate-400 font-normal">(Ty)</span>}
                        </div>
                        <div className="text-xs text-slate-400">{fmtDate(c.created_at)}</div>
                      </div>
                      <p className="mt-2 text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">{c.text}</p>
                    </li>
                  ))}
                  {(comments?.items || []).length === 0 && (
                    <li className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-center text-sm text-slate-500">
                      Jeszcze nikt nie skomentował. Bądź pierwszy!
                    </li>
                  )}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function VoteButton({ active, onClick, icon, count, tone, testid, locked }) {
  const base = "inline-flex items-center gap-2 px-4 py-2 rounded-2xl font-bold text-sm border-2 transition-colors";
  const activeCls = tone === "up"
    ? "bg-green-50 border-green-500 text-green-700"
    : "bg-red-50 border-red-500 text-red-600";
  const idle = "bg-white border-slate-200 text-slate-700 hover:border-slate-900";
  const lockedCls = "bg-slate-50 border-slate-200 text-slate-400";
  return (
    <button onClick={onClick} data-testid={testid} className={`${base} ${locked ? lockedCls : (active ? activeCls : idle)}`}>
      {locked ? <Lock className="w-3.5 h-3.5"/> : icon}
      {count}
    </button>
  );
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("pl-PL", { day: "2-digit", month: "short" });
  } catch { return ""; }
}

/* -------------------- Shared header -------------------- */
function Header({ onBack, title }) {
  return (
    <header className="w-full border-b border-slate-100 sticky top-0 bg-white/85 backdrop-blur z-10">
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <button
          onClick={onBack}
          data-testid="school-back-button"
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 flex-shrink-0"
        >
          <ArrowLeft className="w-4 h-4" /> Wróć
        </button>
        <div className="text-sm text-slate-500 flex items-center gap-2 truncate">
          <School className="w-4 h-4 text-slate-900 flex-shrink-0" /> <span className="truncate">{title}</span>
        </div>
        <div className="w-16 flex-shrink-0" />
      </div>
    </header>
  );
}


/* -------------------- Mute banner (countdown) -------------------- */
function formatRemaining(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function MuteBanner({ mute }) {
  return (
    <div
      data-testid="mute-banner"
      className="mt-4 border-2 border-red-200 bg-red-50 rounded-2xl p-5"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
          <ShieldAlert className="w-5 h-5 text-red-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-extrabold text-red-900 tracking-tight">
            Zostałeś wyciszony
          </h3>
          <p className="mt-1 text-sm text-red-800 leading-relaxed">
            Twój komentarz naruszał regulamin społeczności (wulgaryzmy, mowa
            nienawiści, groźby lub treści zakazane) i został usunięty.
            Nie możesz komentować ani oceniać przez wskazany czas.
          </p>
          <div className="mt-3 flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-red-700" />
            <span className="text-red-900 font-bold" data-testid="mute-countdown">
              Pozostało: {formatRemaining(mute.remaining_seconds)}
            </span>
          </div>
          {mute.violations_count > 0 && (
            <div className="mt-1 text-xs text-red-700">
              Łączna liczba naruszeń: <b>{mute.violations_count}</b> ·
              {" "}Kolejne naruszenia zwiększą czas wyciszenia.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Weekly leaderboard -------------------- */
const RANK_STYLES = [
  { bg: "bg-gradient-to-br from-yellow-300 to-yellow-500", text: "text-slate-900", label: "🥇" },
  { bg: "bg-gradient-to-br from-slate-200 to-slate-400", text: "text-slate-900", label: "🥈" },
  { bg: "bg-gradient-to-br from-orange-300 to-orange-500", text: "text-slate-900", label: "🥉" },
];
const SCHOOL_TYPE_SHORT = {
  primary: "SP", secondary_liceum: "LO", secondary_technikum: "T",
  secondary_zawodowka: "SB", university: "Uczelnia", kindergarten: "Przedszkole", other: "Inna",
};

function WeeklyTop({ onOpenPerson }) {
  const [items, setItems] = useState(null);
  const [role, setRole] = useState("teacher");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/schoolhub/weekly-top", { params: { role, limit: 10 } })
      .then(({ data }) => { if (!cancelled) setItems(data.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [role]);

  return (
    <section className="mt-10 sm:mt-14" data-testid="weekly-top-section">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap mb-5">
        <div>
          <div className="inline-flex items-center gap-2 bg-slate-900 text-white rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider">
            <Flame className="w-3 h-3 text-[#FFD600]" /> Na topie · ten tydzień
          </div>
          <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Najwyżej oceniani <br className="sm:hidden" />
            <span className="relative inline-block">
              {role === "teacher" ? "nauczyciele" : "uczniowie"}
              <span className="absolute -bottom-1 left-0 right-0 h-2 bg-[#FFD600] -z-10 rounded" />
            </span>
          </h2>
          <p className="mt-2 text-sm text-slate-500">Ranking z ostatnich 7 dni · aktualizuje się na żywo</p>
        </div>

        {/* Role toggle */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1" role="tablist" data-testid="weekly-top-role-toggle">
          <button
            onClick={() => setRole("teacher")}
            data-testid="weekly-top-role-teacher"
            className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${
              role === "teacher" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Nauczyciele
          </button>
          <button
            onClick={() => setRole("student")}
            data-testid="weekly-top-role-student"
            className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${
              role === "student" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Uczniowie
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center text-slate-400 text-sm">
          Ładuję ranking...
        </div>
      ) : items.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-3xl p-10 text-center" data-testid="weekly-top-empty">
          <Trophy className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <div className="font-bold text-slate-700">Jeszcze cicho na podium</div>
          <div className="text-sm text-slate-500 mt-1">
            Nikt nie zebrał w tym tygodniu wystarczająco głosów.
            Znajdź swoją szkołę powyżej i zacznij oceniać!
          </div>
        </div>
      ) : (
        <ol className="space-y-2.5" data-testid="weekly-top-list">
          {items.map((it, idx) => {
            const style = RANK_STYLES[idx] || { bg: "bg-slate-100", text: "text-slate-600", label: `#${idx + 1}` };
            return (
              <li key={it.person_id}>
                <button
                  onClick={() => onOpenPerson?.(it.person_id)}
                  data-testid={`weekly-top-row-${idx + 1}`}
                  className="w-full text-left bg-white border border-slate-200 hover:border-slate-900 transition-all rounded-2xl p-3 sm:p-4 flex items-center gap-3 sm:gap-4 group"
                >
                  {/* Rank badge */}
                  <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-2xl ${style.bg} flex items-center justify-center flex-shrink-0 font-extrabold text-base sm:text-lg ${style.text} shadow-sm`}>
                    {idx < 3 ? <span className="text-xl sm:text-2xl">{style.label}</span> : `#${idx + 1}`}
                  </div>

                  {/* Name + school */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-bold text-slate-900 truncate">{it.name}</div>
                      {it.school_type && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
                          {SCHOOL_TYPE_SHORT[it.school_type] || "—"}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500 truncate flex items-center gap-1">
                      <School className="w-3 h-3 flex-shrink-0" />
                      {it.school_name}
                      {it.school_city && <span className="text-slate-400"> · {it.school_city}</span>}
                    </div>
                  </div>

                  {/* Score + votes count */}
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                    <div className="hidden sm:flex items-center gap-1 text-xs text-slate-500">
                      <ThumbsUp className="w-3 h-3 text-green-600"/> {it.weekly_upvotes}
                    </div>
                    <div className="inline-flex items-center gap-1 bg-green-50 border border-green-200 text-green-700 rounded-full px-2.5 py-1 font-extrabold text-sm">
                      +{it.weekly_score}
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-slate-900 transition-colors" />
                  </div>
                </button>
              </li>
            );
          })}
        </ol>
      )}

      {items && items.length > 0 && (
        <p className="mt-4 text-[11px] text-slate-400 text-center">
          Ranking liczy sumę głosów (plus minus minus) z ostatnich 7 dni. Odświeża się przy każdym nowym głosie.
        </p>
      )}
    </section>
  );
}

